import { ArrowDown, ArrowOpen, ArrowUp } from '@sds-system/assets';
import { palette, theme } from '@sds-system/theme';
import type { ColorCode } from '@sds-system/types';
import { getColorValue } from '@sds-system/utils';
import { useRef, useState } from 'react';
import { Button } from '../Button';
import { Column } from '../Column';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Row } from '../Row';
import { Typography } from '../Typography';
import { ListColumn, SnbContainer } from './Snb.styled';
import type { MenuListTypes, SNBProps } from './Snb.types';

export const Snb = ({
  menuLists,
  open = true,
  logo,
  handleRoute,
  pathname,
  styledComponents,
}: SNBProps) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userAgent = window.navigator.userAgent.toLowerCase();
  const [isOpen, setIsOpen] = useState(open);
  const [openListValue, setOpenListValue] = useState(
    pathname === '/' ? '/' : `/${pathname.split('/')[1]}`,
  );
  const [openPopoverValue, setOpenPopoverValue] = useState('');

  const menuButtons = (list: MenuListTypes) => (
    <Row
      br={8}
      css={{
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        backgroundColor:
          (list.href === '/'
            ? pathname === list.href
            : pathname.includes(list.href.split('/')[1] || '')) && '#0000001F',
        padding: '12px 15px',
        width: '100%',
        justifyContent: 'flex-start',
        '& > span': {
          width: '100%',
        },
        '&:hover': {
          backgroundColor: '#0000001F',
        },
      }}
      onClick={() => handleClickMenu(list)}
    >
      <Row
        alignItems='center'
        color='system.white'
        gap={8}
        justifyContent='space-between'
        width='100%'
      >
        <Row alignItems='center' gap={8}>
          <Icon color='icon.tertiary' size='XS' source={list.icon} />
          {isOpen && <Typography fontType='body.regular'>{list.label}</Typography>}
        </Row>
        {isOpen && list.children && (
          <Icon source={list.href === openListValue ? ArrowUp : ArrowDown} />
        )}
      </Row>
    </Row>
  );

  const handleClickMenu = (list: MenuListTypes) => {
    if (isOpen && list.children) {
      if (openListValue === list.href) {
        return setOpenListValue('');
      }
      return setOpenListValue(list.href);
    }
    if (list.children) {
      if (openPopoverValue === list.href) {
        return setOpenPopoverValue('');
      }
      return setOpenPopoverValue(list.href);
    }
    if (!list.children) {
      handleRoute(list.href);
    }
  };

  const handleOpenAndCloseSnb = () => {
    setOpenListValue(pathname === '/' ? '/' : `/${pathname.split('/')[1]}`);
    setOpenPopoverValue('');
    setIsOpen((open) => !open);
  };

  const handleTimeoutClose = () => {
    timeoutRef.current = setTimeout(() => {
      setOpenPopoverValue('');
    }, 500);
  };

  return (
    <SnbContainer
      bgColor={styledComponents?.root?.bgColor || 'gray.700'}
      height='100%'
      width={isOpen ? 256 : 88}
      {...(styledComponents?.root && styledComponents.root)}
    >
      <Row alignItems='flex-end' bgColor='bg.dimmer.20' css={{ padding: '24px 30px' }} height={100}>
        {isOpen ? logo.open : logo.close}
      </Row>
      <Column p={16}>
        {menuLists.map((list) => (
          <ListColumn
            key={list.label}
            maxHeight={
              list.href === openListValue
                ? `calc(${(list.children?.length || 0) * 44}px + 48px)`
                : 48
            }
          >
            {isOpen || userAgent.includes('android') || userAgent.includes('iphone') ? (
              menuButtons(list)
            ) : (
              <Row
                onMouseEnter={() => {
                  if (timeoutRef.current) clearTimeout(timeoutRef.current);
                  if (!list.children) {
                    return setOpenPopoverValue('');
                  }
                  if (openPopoverValue !== list.href) {
                    return handleClickMenu(list);
                  }
                }}
                onMouseLeave={handleTimeoutClose}
              >
                {menuButtons(list)}
              </Row>
            )}
            {isOpen &&
              list.children?.map((child) => (
                <Button
                  css={{
                    padding: '8px 0px 8px 48px',
                    width: '100%',
                    justifyContent: 'flex-start',
                    '&:hover span': {
                      color: getColorValue(palette.yellow.point as ColorCode, theme),
                    },
                  }}
                  key={child.label}
                  onClick={() => handleRoute(child.href)}
                  size={'M'}
                  variant='ghost.assistive'
                >
                  <Typography
                    color={
                      (
                        child.href === '/'
                          ? pathname === child.href
                          : pathname.includes(child.href.split('/')[2] || child.href)
                      )
                        ? 'yellow.point'
                        : 'text.assistance'
                    }
                    fontType='body2.regular'
                  >
                    {child.label}
                  </Typography>
                </Button>
              ))}
            {!isOpen && list.children && openPopoverValue === list.href && (
              <Column
                bgColor='bg.primary'
                border='1 solid border.interactive.secondary'
                br={8}
                css={{ overflow: 'hidden' }}
                ml={80}
                onMouseEnter={() => {
                  if (timeoutRef.current) clearTimeout(timeoutRef.current);
                }}
                onMouseLeave={handleTimeoutClose}
                position='absolute'
                py={8}
                zIndex={1}
              >
                {list.children?.map((child) => (
                  <Button
                    css={{
                      width: '100%',
                      borderRadius: 0,
                      padding: '0 12px',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        backgroundColor: getColorValue(palette.gray[100] as ColorCode, theme),
                      },
                    }}
                    key={child.label}
                    onClick={() => {
                      handleRoute(child.href);
                      setOpenPopoverValue('');
                    }}
                    size={'M'}
                    variant='ghost.assistive'
                  >
                    <Typography
                      color={
                        (
                          child.href === '/'
                            ? pathname === child.href
                            : pathname.includes(child.href.split('/')[2] || child.href)
                        )
                          ? 'text.info'
                          : 'text.primary'
                      }
                      fontType='body.regular'
                    >
                      {child.label}
                    </Typography>
                  </Button>
                ))}
              </Column>
            )}
          </ListColumn>
        ))}
        <Row justifyContent='flex-end' mt={24} px={8}>
          <IconButton
            color='system.white'
            css={{
              '&:hover': {
                color: getColorValue(palette.yellow.point as ColorCode, theme),
              },
            }}
            onClick={handleOpenAndCloseSnb}
            size='M'
            variant='ghost.primary'
          >
            <Icon
              css={{ transform: isOpen ? 'rotateY(180deg)' : 'rotateY(0)' }}
              source={ArrowOpen}
            />
          </IconButton>
        </Row>
      </Column>
    </SnbContainer>
  );
};
